/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/metadata/numeric
 * @desc Column Definition for metadata numeric
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';

export const getMetadataNumericColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      const variableTemplate =
        GridSmartTableProtocolConditionService.getMetadataVariableTemplate(
          experimentOrProtocol,
          metadataLabel,
        );

      const constant =
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.type.numeric,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolConditionService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolConditionService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel}`,
        isConstant: !!constant,
        experimentOrProtocol,
        metadata: variableTemplate || constant,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolConditionService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      return metadata?.printValue;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const newValue = params.newValue;

      let amount = null;
      let unit = null;

      if (typeof newValue === 'string') {
        const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
        const match = newValue.replace(',', '.').match(regex);
        if (!match) {
          GridSmartTableProtocolConditionService.triggerValidationError(
            metadataLabel,
          );
          return false;
        }
        amount = match[1];
        unit = match[2].trim();
      } else if (typeof newValue === 'number') {
        amount = newValue.toString();
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      if (!metadata) {
        return GridSmartTableProtocolConditionService.createMetadata(
          params,
          experimentOrProtocol,
          parentEntityClass,
          metadataType,
          metadataLabel,
          protocolValueName,
          {
            number: amount,
            unit,
          },
        );
      }

      if (amount == null) {
        return GridSmartTableProtocolConditionService.updateEntity(
          params,
          metadata.entityName,
          metadata.guid,
          {
            number: null,
          },
        );
      }

      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        metadata.entityName,
        metadata.guid,
        {
          number: amount,
          unit: unit && unit.length > 0 ? unit : undefined,
        },
      );
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      if (
        metadata?.number !== undefined &&
        metadata?.number !== null
      ) {
        return `${metadata.number} ${metadata.unitDisplayValue || ''}`;
      }

      return (
        <DataGridPlaceholder
          params={params}
          editable={GridSmartTableProtocolConditionService.getMetadataEditable(
            params,
            experimentOrProtocol,
            metadataType,
            metadataLabel,
            parentEntityClass,
            protocolValueName,
          )}
          placeholder={
            GridSmartTableProtocolConditionService.getMetadataEditable(
              params,
              experimentOrProtocol,
              metadataType,
              metadataLabel,
              parentEntityClass,
              protocolValueName,
            )
              ? 'Enter'
              : ' '
          }
        />
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolConditionService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
  };
};
